/* Progressive enhancement. Shopify remains the source of product/cart state. */
(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const strings = config.strings || {};
  const listen = (target, event, handler, signal, options = {}) => target.addEventListener(event, handler, { ...options, signal });
  class ThemeElement extends HTMLElement {
    connectedCallback() { this.events?.abort(); this.events = new AbortController(); this.setup?.(this.events.signal); }
    disconnectedCallback() { this.events?.abort(); }
  }

  class Quantity extends ThemeElement {
    setup(signal) {
      const input = this.querySelector('input');
      if (!input) return;
      const sync = () => {
        this.querySelector('[data-step="-1"]').disabled = Number(input.value) <= Number(input.min || 0);
        this.querySelector('[data-step="1"]').disabled = input.max !== '' && Number(input.value) >= Number(input.max);
      };
      listen(this, 'click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button || button.disabled) return;
        if (!Number.isFinite(input.valueAsNumber)) input.value = input.min || '1';
        Number(button.dataset.step) > 0 ? input.stepUp() : input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, signal);
      listen(input, 'change', sync, signal);
      sync();
    }
  }

  class Slider extends ThemeElement {
    setup(signal) {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.show(0);
      listen(this, 'click', (event) => {
        if (event.target.closest('[data-prev]')) this.show(this.index - 1);
        if (event.target.closest('[data-next]')) this.show(this.index + 1);
        const dot = event.target.closest('[data-slide-to]');
        if (dot) this.show(Number(dot.dataset.slideTo));
        const pause = event.target.closest('[data-pause]');
        if (pause) {
          this.paused = !this.paused;
          pause.setAttribute('aria-label', this.paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
          pause.setAttribute('aria-pressed', String(this.paused));
          this.schedule();
        }
      }, signal);
      listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); }, signal);
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); }, signal);
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); }, signal);
      listen(this, 'focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, signal);
      listen(document, 'visibilitychange', () => this.schedule(), signal);
      listen(this.reduced, 'change', () => this.schedule(), signal);
      listen(this, 'shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) { this.paused = true; this.show(index); this.schedule(); }
      }, signal);
      this.schedule();
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.querySelectorAll('[data-slide-to]').forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.index)));
    }
    schedule() {
      clearInterval(this.timer);
      const autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      if (autoplay && !this.paused && !this.focused && !this.hovered && !document.hidden && !this.reduced.matches && !window.Shopify?.designMode) {
        this.timer = setInterval(() => this.show(this.index + 1), Number(this.dataset.interval) || 6000);
      }
    }
    disconnectedCallback() { super.disconnectedCallback(); clearInterval(this.timer); }
  }

  class ImageAccordion extends ThemeElement {
    setup(signal) {
      const show = (details) => {
        if (!details?.open) return;
        this.querySelectorAll('[data-accordion-image]').forEach((image) => { image.hidden = image.dataset.accordionImage !== details.dataset.imageIndex; });
      };
      this.querySelectorAll('details').forEach((details) => listen(details, 'toggle', () => show(details), signal));
      listen(this, 'shopify:block:select', (event) => {
        const details = event.target.closest('details');
        if (details) { details.open = true; show(details); }
      }, signal);
    }
  }

  class Gallery extends ThemeElement {
    setup(signal) {
      this.show(this.dataset.activeMedia || this.querySelector('[data-media-id]')?.dataset.mediaId);
      const dialog = this.querySelector('dialog');
      listen(this, 'click', (event) => {
        const thumbnail = event.target.closest('[data-thumbnail]');
        if (thumbnail) { event.preventDefault(); this.show(thumbnail.dataset.thumbnail); }
        const direction = event.target.closest('[data-gallery-prev], [data-gallery-next]');
        if (direction) {
          const items = [...this.querySelectorAll('[data-media-id]')];
          const index = items.findIndex((item) => !item.hidden);
          const step = direction.hasAttribute('data-gallery-next') ? 1 : -1;
          this.show(items[(index + step + items.length) % items.length]?.dataset.mediaId);
        }
        const zoom = event.target.closest('[data-zoom]');
        if (zoom && dialog?.showModal) {
          event.preventDefault();
          const source = zoom.querySelector('img');
          if (!source) return;
          const image = new Image();
          image.src = zoom.href;
          image.alt = source.alt;
          dialog.querySelector('[data-zoom-content]').replaceChildren(image);
          this.zoomTrigger = zoom;
          dialog.showModal();
        }
        if (event.target.closest('[data-close-dialog]') || event.target === dialog) dialog.close();
      }, signal);
      if (dialog) listen(dialog, 'close', () => this.zoomTrigger?.focus(), signal);
      if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {
          if (window.Shopify.ModelViewerUI && this.isConnected) {
            this.models = [...this.querySelectorAll('model-viewer')].map((model) => new window.Shopify.ModelViewerUI(model));
          }
        } }]);
      }
    }
    show(id) {
      this.querySelectorAll('[data-media-id]').forEach((media) => {
        media.hidden = media.dataset.mediaId !== String(id);
        if (media.hidden) {
          media.querySelectorAll('video').forEach((video) => video.pause());
          media.querySelectorAll('iframe').forEach((frame) => {
            try {
              const origin = new URL(frame.src).origin;
              frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
              frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), origin);
            } catch { /* No external player to pause. */ }
          });
        }
      });
      this.querySelectorAll('[data-thumbnail]').forEach((thumbnail) => {
        if (thumbnail.dataset.thumbnail === String(id)) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      const items = [...this.querySelectorAll('[data-media-id]')];
      const output = this.querySelector('[data-gallery-status]');
      if (output) output.textContent = (items.findIndex((item) => !item.hidden) + 1) + ' / ' + items.length;
    }
    disconnectedCallback() { super.disconnectedCallback(); this.models?.forEach((model) => model.destroy?.()); }
  }

  class Product extends ThemeElement {
    setup(signal) {
      this.initialOptions = [...this.querySelectorAll('[data-option-select]')].map((input) => input.value);
      listen(this, 'click', (event) => {
        const pill = event.target.closest('[data-option-value-id]');
        if (pill && pill.getAttribute('aria-pressed') !== 'true') {
          const select = pill.closest('[data-option-group]').querySelector('[data-option-select]');
          select.value = pill.dataset.optionValueId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const offer = event.target.closest('[data-quantity-offer]');
        const quantity = this.querySelector('input[name="quantity"]');
        if (offer && !offer.disabled && quantity) {
          quantity.value = offer.dataset.quantityOffer;
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, signal);
      listen(this, 'change', (event) => {
        if (event.target.matches('[data-option-select]')) this.updateVariant(event.target);
        if (event.target.matches('[data-selling-plan]')) this.updatePlan();
        if (event.target.matches('input[name="quantity"]')) this.syncOffers();
      }, signal);
      listen(this, 'shopify:block:select', (event) => {
        const details = event.target.querySelector('details');
        if (details) details.open = true;
      }, signal);
      this.syncOffers();
      this.updatePlan();
    }
    syncOffers() {
      const quantity = this.querySelector('input[name="quantity"]');
      this.querySelectorAll('[data-quantity-offer]').forEach((offer) => {
        offer.setAttribute('aria-pressed', String(!offer.disabled && Number(offer.dataset.quantityOffer) === Number(quantity?.value)));
      });
    }
    updatePlan() {
      const select = this.querySelector('[data-selling-plan]');
      const output = this.querySelector('[data-selling-plan-price]');
      if (select && output) output.textContent = select.selectedOptions[0]?.dataset.price || '';
    }
// HGM JS chunk 0
    async updateVariant(changed) {
      this.request?.abort();
      this.request = new AbortController();
      const selected = [...this.querySelectorAll('[data-option-select]')].map((input) => input.value);
      const target = changed.selectedOptions[0]?.dataset.productUrl || this.dataset.url;
      const url = new URL(target, window.location.origin);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('option_values', selected.join(','));
      if (url.pathname !== new URL(this.dataset.url, window.location.origin).pathname) {
        window.location.assign(url);
        return;
      }
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const customerUrl = new URL(url);
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      const buyButtons = [...this.querySelectorAll('[name="add"], [data-buy-now]')];
      const wasDisabled = buyButtons.map(button => button.disabled);
      buyButtons.forEach(button => { button.disabled = true; });
      const quantity = this.querySelector('input[name="quantity"]')?.value;
      const plan = this.querySelector('[data-selling-plan]')?.value;
      const error = this.querySelector('[data-variant-error]');
      if (error) error.hidden = true;
      try {
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error(strings.error);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('hgm-product');
        if (!next) throw new Error(strings.error);
        const input = next.querySelector('input[name="quantity"]');
        if (input && quantity) {
          const min = Number(input.dataset.quantityMin || input.min || 1);
          const max = Number(input.dataset.quantityMax || input.max || Infinity);
          const step = Number(input.dataset.quantityStep || input.step || 1);
          input.value = quantity;
          if (!input.checkValidity() || !Number.isInteger(Number(quantity)) || Number(quantity) < min || Number(quantity) > max || (Number(quantity) - min) % step !== 0) input.value = String(min);
        }
        const nextPlan = next.querySelector('[data-selling-plan]');
        if (nextPlan && [...nextPlan.options].some((option) => option.value === plan)) nextPlan.value = plan;
        this.replaceWith(next);
        const focus = next.querySelector('[data-option-group="' + changed.dataset.position + '"] [aria-pressed="true"]') || next.querySelector('[data-position="' + changed.dataset.position + '"]');
        focus?.focus({ preventScroll: true });
        if (next.dataset.variantId) {
          customerUrl.searchParams.delete('option_values');
          customerUrl.searchParams.set('variant', next.dataset.variantId);
        }
        customerUrl.hash = window.location.hash;
        window.history.replaceState({}, '', customerUrl);
        window.Shopify?.PaymentButton?.init();
        document.dispatchEvent(new CustomEvent('hgm:variant-change', { detail: { variantId: next.dataset.variantId, sectionId: next.dataset.sectionId } }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.querySelectorAll('[data-option-select]').forEach((input, i) => { input.value = this.initialOptions[i]; });
        if (error) { error.textContent = strings.error; error.hidden = false; }
        buyButtons.forEach((button, index) => { button.disabled = wasDisabled[index]; });
        this.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() { super.disconnectedCallback(); this.request?.abort(); }
  }

  class Recommendations extends ThemeElement {
    setup() {
      if (this.querySelector('.product-grid')) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    async load() {
      this.request = new AbortController();
      try {
        const response = await fetch(this.dataset.url, { signal: this.request.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('hgm-recommendations');
        if (content?.querySelector('.product-grid')) this.innerHTML = content.innerHTML;
        else this.hidden = true;
      } catch (error) { if (error.name !== 'AbortError') this.hidden = true; }
    }
    disconnectedCallback() { super.disconnectedCallback(); this.observer?.disconnect(); this.request?.abort(); }
  }


  // Serialize theme cart requests so an older response cannot overwrite a newer cart.
  let cartQueue = Promise.resolve();
  const queueCart = (work) => {
    const next = cartQueue.then(work);
    cartQueue = next.catch(() => {});
    return next;
  };
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  }

  class CartDrawer extends ThemeElement {
    setup(signal) {
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-cart-drawer-content]');
      this.status = this.querySelector('[data-cart-status]');
      listen(this, 'click', (event) => {
        if (event.target.closest('[data-cart-close]')) this.dialog.close();
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }
        if (event.target.closest('[data-cart-retry]')) this.refresh();
        const edit = event.target.closest('[data-checkout-edit]');
        if (edit && !this.busy) {
          const line = edit.closest('[data-cart-line]');
          const key = line.dataset.cartLine;
          if (this.editingLines.has(key)) this.editingLines.delete(key);
          else this.editingLines.add(key);
          this.applyMode();
        }
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) this.change(remove.closest('[data-cart-line]'), 0, remove);
      }, signal);
      listen(this, 'change', (event) => {
        if (!event.target.matches('[name="updates[]"]') || this.busy) return;
        const input = event.target;
        if (!input.checkValidity()) {
          this.showError(strings.quantityError);
          input.value = input.defaultValue;
          input.dispatchEvent(new Event('change', { bubbles: false }));
          return;
        }
        this.change(input.closest('[data-cart-line]'), input.valueAsNumber, document.activeElement);
      }, signal);
      listen(this, 'keydown', (event) => {
        if (event.key === 'Tab') {
          const controls = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
            .filter((control) => control.tabIndex >= 0 && !control.disabled && !control.closest('[inert], [hidden]') && control.getClientRects().length);
          const first = controls[0];
          const last = controls.at(-1);
          if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
            event.preventDefault();
            (event.shiftKey ? last : first)?.focus();
          }
        }
        if (event.key === 'Enter' && event.target.matches('[name="updates[]"]')) {
          event.preventDefault();
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, signal);
      listen(this, 'submit', (event) => {
        if (!event.target.matches('#CartDrawerForm')) return;
        // Enter in a quantity field updates the line, never unexpectedly checks out.
        if (this.busy || event.submitter?.name !== 'checkout') {
          event.preventDefault();
          return;
        }
        const pageNote = document.querySelector('[data-cart-page-section] [name="note"]');
        if (pageNote) {
          const note = event.target.querySelector('[name="note"]') || document.createElement('input');
          note.type = 'hidden';
          note.name = 'note';
          note.value = pageNote.value;
          event.target.append(note);
        }
      }, signal);
      listen(this.dialog, 'close', () => {
        document.documentElement.classList.remove('cart-drawer-open');
        (this.trigger?.isConnected ? this.trigger : document.querySelector('[data-cart-open]'))?.focus({ preventScroll: true });
      }, signal);
    }
    open(trigger) {
      if (!this.dialog.showModal) { window.location.assign(config.cartUrl); return; }
      if (this.dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.checkoutMode = Boolean(trigger?.hasAttribute('data-buy-now'));
      this.checkoutCopy = this.checkoutMode ? { ...trigger.dataset } : {};
      this.editingLines = new Set();
      for (const [target, source] of [['--checkout-accent', 'checkoutAccent'], ['--checkout-button-text', 'checkoutButtonText'], ['--checkout-background', 'checkoutBackground'], ['--checkout-text', 'checkoutText']]) {
        const value = this.checkoutCopy[source];
        if (value) this.dialog.style.setProperty(target, value);
        else this.dialog.style.removeProperty(target);
      }
      this.applyMode();
      this.dialog.showModal();
      document.documentElement.classList.add('cart-drawer-open');
    }
    applyMode() {
      this.dialog.classList.toggle('cart-drawer--checkout', Boolean(this.checkoutMode));
      const title = this.querySelector('[data-cart-title]');
      if (title) title.textContent = this.checkoutMode ? (this.checkoutCopy.checkoutTitle || title.dataset.defaultLabel) : title.dataset.defaultLabel;
      const intro = this.querySelector('[data-checkout-intro]');
      if (intro) {
        intro.textContent = this.checkoutMode ? this.checkoutCopy.checkoutIntro || '' : '';
        intro.hidden = !intro.textContent;
      }
      const button = this.querySelector('[data-cart-checkout]');
      if (button) button.querySelector('[data-cart-checkout-label]').textContent = this.checkoutMode ? (this.checkoutCopy.checkoutLabel || button.dataset.defaultLabel) : button.dataset.defaultLabel;
      for (const [selector, copy] of [['[data-checkout-brand]', 'checkoutBrand'], ['[data-checkout-note]', 'checkoutNote'], ['[data-checkout-summary-note]', 'checkoutSummaryNote'], ['[data-checkout-continue]', 'checkoutContinueLabel']]) {
        const element = this.querySelector(selector);
        if (!element) continue;
        element.textContent = this.checkoutMode ? this.checkoutCopy[copy] || '' : '';
        element.hidden = !element.textContent;
      }
      const subtotal = this.querySelector('[data-cart-subtotal-label]');
      if (subtotal) subtotal.textContent = this.checkoutMode ? this.checkoutCopy.checkoutSubtotalLabel || subtotal.dataset.defaultLabel : subtotal.dataset.defaultLabel;
      this.querySelectorAll('[data-checkout-icon], [data-checkout-quantity]').forEach(element => { element.hidden = !this.checkoutMode; });
      this.querySelectorAll('[data-cart-view], [data-cart-tax-note]').forEach(element => { element.hidden = Boolean(this.checkoutMode); });
      this.querySelectorAll('[data-cart-line]').forEach(line => {
        const editing = this.editingLines?.has(line.dataset.cartLine);
        const edit = line.querySelector('[data-checkout-edit]');
        edit.hidden = !this.checkoutMode;
        edit.textContent = editing ? this.checkoutCopy.checkoutDoneLabel || 'Done editing' : this.checkoutCopy?.checkoutEditLabel || 'Edit selection';
        edit.setAttribute('aria-expanded', String(Boolean(editing)));
        line.querySelector('.cart-drawer-item__controls').hidden = Boolean(this.checkoutMode && !editing);
      });
      if (!this.checkoutMode) {
        this.editingLines?.clear();
      }
    }
    setBusy(busy) {
      this.busy = busy;
      if (busy && this.content.contains(document.activeElement)) this.querySelector('[data-cart-close]').focus({ preventScroll: true });
      this.content.inert = busy;
      this.content.setAttribute('aria-busy', String(busy));
      this.querySelector('[data-cart-retry]').disabled = busy;
      if (busy) {
        this.querySelector('[data-cart-error]').hidden = true;
        this.status.textContent = strings.loading;
      }
    }
    showError(message, recovery = false) {
      this.querySelector('[data-cart-error-message]').textContent = message || strings.error;
      this.querySelector('[data-cart-error]').hidden = false;
      this.querySelector('[data-cart-recovery]').hidden = !recovery;
      if (recovery) this.content.hidden = true;
      this.status.textContent = '';
    }
    sectionIds() {
      return [this.dataset.sectionId, document.querySelector('[data-cart-page-section]')?.dataset.cartPageSection].filter(Boolean);
    }
    render(sections) {
      const html = sections?.[this.dataset.sectionId];
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-cart-drawer-content]');
      if (!next) return false;
      const scroller = this.querySelector('.cart-drawer__items');
      const scrollTop = scroller?.scrollTop || 0;
      this.content.replaceChildren(...next.childNodes);
      this.content.dataset.itemCount = next.dataset.itemCount;
      this.content.hidden = false;
      this.applyMode();
      const nextScroller = this.querySelector('.cart-drawer__items');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      updateCartCount(Number(next.dataset.itemCount));
      const cartPage = document.querySelector('[data-cart-page-section]');
      if (cartPage) {
        const pageHTML = sections?.[cartPage.dataset.cartPageSection];
        const nextPage = pageHTML && new DOMParser().parseFromString(pageHTML, 'text/html').querySelector('[data-cart-page-section]');
        // Avoid submitting stale cart-page quantities after a drawer edit.
        if (!nextPage) { window.location.assign(config.cartUrl); return true; }
        const note = cartPage.querySelector('[name="note"]')?.value;
        cartPage.replaceChildren(...nextPage.childNodes);
        if (note !== undefined && cartPage.querySelector('[name="note"]')) cartPage.querySelector('[name="note"]').value = note;
      }
      document.dispatchEvent(new CustomEvent('hgm:cart-update', { detail: { item_count: Number(next.dataset.itemCount) } }));
      return true;
    }
    async sync() {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', this.sectionIds().join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok || !this.render(await response.json())) throw new Error(strings.refreshError);
    }
    async apply(result) {
      // A successful cart mutation can still return null section HTML. Only retry the read.
      if (!this.render(result.sections)) await this.sync();
    }
    refresh() {
      return queueCart(async () => {
        this.setBusy(true);
        try { await this.sync(); this.status.textContent = ''; }
        catch { this.showError(strings.refreshError, true); }
        finally { this.setBusy(false); }
      });
    }
    change(line, quantity, active) {
      if (!line || this.busy) return;
      const key = line.dataset.cartLine;
      const index = Number(line.dataset.cartIndex);
      const selector = active?.matches('[data-step]') ? '[data-step="' + active.dataset.step + '"]' : active?.matches('[data-cart-remove]') ? '[data-cart-remove]' : 'input';
      this.setBusy(true);
      queueCart(async () => {
        let message = strings.updated;
        try {
          const response = await fetch(config.cartChange, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: this.sectionIds(), sections_url: window.location.pathname + window.location.search })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || strings.error);
          await this.apply(result);
          if (quantity > 0 && result.items && result.items[index]?.quantity !== quantity) message = strings.quantityAdjusted;
          this.status.textContent = message;
        } catch (error) {
          // Even an inventory error or lost response may have changed the server cart.
          try { await this.sync(); this.showError(error.message); }
          catch { this.showError(strings.refreshError, true); }
        } finally {
          this.setBusy(false);
          if (this.dialog.open && (document.activeElement === active || document.activeElement === document.body || !active?.isConnected)) {
            const lines = [...this.querySelectorAll('[data-cart-line]')];
            const nextLine = lines.find((item) => item.dataset.cartLine === key) || lines[Math.min(index, lines.length - 1)];
            const target = nextLine?.querySelector(selector);
            const fallback = this.checkoutMode ? nextLine?.querySelector('[data-checkout-edit]') : nextLine?.querySelector('input');
            (target && !target.disabled && target.getClientRects().length ? target : fallback || this.querySelector('[data-cart-close]')).focus({ preventScroll: true });
          }
        }
      });
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.dialog?.open) document.documentElement.classList.remove('cart-drawer-open');
    }
  }

  const definitions = { 'hgm-cart-drawer': CartDrawer, 'hgm-quantity': Quantity, 'hgm-slider': Slider, 'hgm-image-accordion': ImageAccordion, 'hgm-gallery': Gallery, 'hgm-product': Product, 'hgm-recommendations': Recommendations };
  Object.entries(definitions).forEach(([name, definition]) => { if (!customElements.get(name)) customElements.define(name, definition); });


  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('[data-product-form]') || !config.cartAdd || event.submitter?.name === 'checkout') return;
    event.preventDefault();
    if (form.dataset.submitting === 'true') return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    const label = button?.textContent;
    const error = form.querySelector('[data-form-error]');
    const drawer = document.querySelector('hgm-cart-drawer');
    const body = new FormData(form);
    const productInfo = form.closest('hgm-product')?.querySelector('.product-info');
    const wasInert = productInfo?.inert;
    if (drawer) {
      body.set('sections', drawer.sectionIds().join(','));
      body.set('sections_url', window.location.pathname + window.location.search);
    }
    if (error) error.hidden = true;
    form.dataset.submitting = 'true';
    form.setAttribute('aria-busy', 'true');
    if (productInfo) productInfo.inert = true;
    if (button) { button.disabled = true; button.textContent = strings.adding; }
    let added = false;
    try {
      await queueCart(async () => {
        const response = await fetch(config.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok) {
          if (drawer) await drawer.sync().catch(() => {});
          throw new Error(result.description || result.message || strings.error);
        }
        added = true;
        if (drawer) {
          drawer.setBusy(true);
          try { await drawer.apply(result); drawer.status.textContent = strings.added; }
          catch { drawer.showError(strings.refreshError, true); }
          finally { drawer.setBusy(false); }
        } else window.location.assign(config.cartUrl);
      });
    } catch (err) {
      if (error) { error.textContent = err.message || strings.error; error.hidden = false; }
    } finally {
      form.removeAttribute('aria-busy');
      delete form.dataset.submitting;
      if (productInfo) productInfo.inert = wasInert;
      if (button) { button.disabled = false; button.textContent = label; }
    }
    if (added) drawer?.open(button);
  });

  document.addEventListener('click', (event) => {
    const cartTrigger = event.target.closest('[data-cart-open]');
    const drawer = document.querySelector('hgm-cart-drawer');
    if (cartTrigger && drawer && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      drawer.open(cartTrigger);
      drawer.refresh();
    }
    document.querySelectorAll('.nav-dropdown[open],.mobile-nav[open],.filter-panel[open]').forEach((details) => { if (!details.contains(event.target)) details.open = false; });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const details = document.activeElement?.closest('.nav-dropdown[open],.mobile-nav[open],.filter-panel[open]');
    if (details) { details.open = false; details.querySelector('summary')?.focus(); }
  });
  document.addEventListener('shopify:block:select', (event) => {
    const accordion = event.target.closest('details.accordion');
    if (accordion) accordion.open = true;
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) document.querySelector('hgm-cart-drawer')?.refresh(); });
})();

// HGM JS chunk 1
